import {dateFromServerDate, fromDisplayDate} from '../util/dates'

import {ActionCardType} from '../views/campagne/partials/actions/actioncard'
import endOfDay from 'date-fns/endOfDay'
import getDistance from 'geolib/es/getDistance'
import isAfter from 'date-fns/isAfter'
import isBefore from 'date-fns/isBefore'
import isSameDay from 'date-fns/isSameDay'
import isWithinInterval from 'date-fns/isWithinInterval'
import setHours from 'date-fns/setHours'
import setMinutes from 'date-fns/setMinutes'
import {startOfDay} from 'date-fns'

export const DISPLAY_DATE_FORMAT: string = 'dd/MM'
const CATEGORY_PREFIX = 'category_'

export type Filters = {
	geometry?: {
		lat: number
		lng: number
	}
	radius: string
	keyword: string
	date_start: string
	date_end: string
	date_type: 'on' | 'between'
	[key: string]: any
}

const onlyStartDate = (event: ActionCardType, startDate: string): boolean => {
	const eventStartDate = dateFromServerDate(event?.date_start)
	const eventEndDate = dateFromServerDate(event?.date_end)
	return (
		(isSameDay(
			eventStartDate,
			fromDisplayDate(startDate, DISPLAY_DATE_FORMAT)
		) ||
			isAfter(
				eventStartDate,
				fromDisplayDate(startDate, DISPLAY_DATE_FORMAT)
			)) &&
		(isSameDay(eventEndDate, fromDisplayDate(startDate, DISPLAY_DATE_FORMAT)) ||
			isAfter(eventEndDate, fromDisplayDate(startDate, DISPLAY_DATE_FORMAT)))
	)
}

const onlyEndDate = (event: ActionCardType, endDate: string): boolean => {
	const eventStartDate = dateFromServerDate(event?.date_start)
	const eventEndDate = dateFromServerDate(event?.date_end)
	return (
		(isSameDay(eventStartDate, fromDisplayDate(endDate, DISPLAY_DATE_FORMAT)) ||
			isBefore(
				eventStartDate,
				endOfDay(fromDisplayDate(endDate, DISPLAY_DATE_FORMAT))
			)) &&
		(isSameDay(eventEndDate, fromDisplayDate(endDate, DISPLAY_DATE_FORMAT)) ||
			isBefore(
				eventEndDate,
				endOfDay(fromDisplayDate(endDate, DISPLAY_DATE_FORMAT))
			))
	)
}

const startAndEndDate = (
	event: ActionCardType,
	startDate: string,
	endDate: string
): boolean => {
	const eventStartDate = dateFromServerDate(event?.date_start)
	const eventEndDate = dateFromServerDate(event?.date_end)
	const testEventStart: boolean =
		(isSameDay(
			eventStartDate,
			fromDisplayDate(startDate, DISPLAY_DATE_FORMAT)
		) ||
			isAfter(
				eventStartDate,
				fromDisplayDate(startDate, DISPLAY_DATE_FORMAT)
			)) &&
		(isSameDay(eventStartDate, fromDisplayDate(endDate, DISPLAY_DATE_FORMAT)) ||
			isBefore(
				eventStartDate,
				endOfDay(fromDisplayDate(endDate, DISPLAY_DATE_FORMAT))
			))
	const testEventEnd: boolean =
		(isSameDay(eventEndDate, fromDisplayDate(startDate, DISPLAY_DATE_FORMAT)) ||
			isAfter(eventEndDate, fromDisplayDate(startDate, DISPLAY_DATE_FORMAT))) &&
		(isSameDay(eventEndDate, fromDisplayDate(endDate, DISPLAY_DATE_FORMAT)) ||
			isBefore(
				eventEndDate,
				endOfDay(fromDisplayDate(endDate, DISPLAY_DATE_FORMAT))
			))
	return testEventStart && testEventEnd
}

const isInRange = (date: string, event: ActionCardType): boolean => {
	if (!date) return false

	const filterDate = fromDisplayDate(date, DISPLAY_DATE_FORMAT)
	const eventStartDate = dateFromServerDate(event?.date_start)
	const eventEndDate = dateFromServerDate(event?.date_end)

	if (isSameDay(eventStartDate, filterDate)) return true

	if (isSameDay(eventEndDate, filterDate)) return true

	if (
		isAfter(filterDate, startOfDay(eventStartDate)) &&
		isBefore(filterDate, endOfDay(eventEndDate))
	)
		return true

	return false
}

export const isDateMatching = (
	filters: Filters,
	event: ActionCardType
): boolean => {
	if (!filters) return true
	const dateType = filters.date_type
	const dateIsBetween = dateType === 'between'
	if (dateIsBetween) {
		// between 2 dates
		const startDate = filters?.date_start
		const endDate = filters?.date_end
		if ((!startDate || startDate === '') && (!endDate || endDate === '')) {
			// no date data was entered
			return true
		}

		if (!event || !event.date_end) return false

		if (startDate && startDate !== '' && (!endDate || endDate === '')) {
			// only a start date was entered
			// event start date must be >= startDate
			// event end date must be >= startDate
			return onlyStartDate(event, startDate)
		}

		if ((!startDate || startDate === '') && endDate && endDate !== '') {
			// only an end date was entered
			// event start date must be <= endDate
			// event end date must be <= endDate
			return onlyEndDate(event, endDate)
		}

		if (startDate && startDate !== '' && endDate && endDate !== '') {
			// start and end date where entered
			// event start date must be >= startDate and <= endDate
			// event end date must be >= startDate and <= endDate
			return startAndEndDate(event, startDate, endDate)
		}

		return false
	}

	// on an exact date
	return isInRange(filters?.date_start, event)
}

export const filterEvents = (
	filters: Filters,
	events: Array<ActionCardType>
) => {
	if (!filters) return events
	const categories = Object.keys(filters)
		.filter((name) => name.startsWith(CATEGORY_PREFIX) && filters[name])
		.map((key) => key.substring(CATEGORY_PREFIX.length))

	// filter the events
	const filteredEvents: Array<ActionCardType> = [...events].filter((event) => {
		// filter by zip/city => geometry
		if (
			event.latitude &&
			event.longitude &&
			filters.geometry &&
			filters.geometry.lat &&
			filters.geometry.lng &&
			filters.radius
		) {
			if (event.latitude === '0' && event.longitude === '0') return false
			const filterIsInRange: boolean =
				getDistance(
					{latitude: filters.geometry.lat, longitude: filters.geometry.lng},
					{latitude: Number(event.latitude), longitude: Number(event.longitude)}
				) <=
				Number(filters.radius) * 1000
			if (!filterIsInRange) return false
		}

		// filter by category
		if (categories.length > 0) {
			const isInCategory: boolean = categories.indexOf(event.category) > -1
			if (!isInCategory) return false
		}

		// filter by title
		if (filters.keyword) {
			const isInTitle: boolean = event.title
				.toLowerCase()
				.includes(filters.keyword.toLowerCase())
			if (!isInTitle) return false
		}

		// filter by date
		if (
			(filters.date_type === 'on' && filters.date_start !== null) ||
			(filters.date_type === 'between' &&
				filters.date_start !== null &&
				filters.date_end !== null)
		) {
			const dateIsMatching: boolean = isDateMatching(filters, event)
			if (!dateIsMatching) return false
		}

		return true
	})

	return filteredEvents
}
